import { useState } from 'react';
import { Sparkles, Clock } from 'lucide-react';
import type { TfaDeliberationResponse } from '@edison/api-client-react';

const QUESTIONS_ORDERED = [
  { key: 'systematic_investigation', label: 'Experimental Approach', short: 'Experimental' },
  { key: 'technical_uncertainty', label: 'Technical Uncertainty', short: 'Uncertain' },
  { key: 'new_knowledge', label: 'New Knowledge', short: 'New Knowledge' },
  { key: 'innovative', label: 'Advance in Capability', short: 'Advance' },
  { key: 'project_specific_uncertainty', label: 'Project-Specific Uncertainty', short: 'Specific' },
] as const;

interface Props {
  synthesis: string | null;
  deliberations: TfaDeliberationResponse[];
}

export function TfaSynthesisView({ synthesis, deliberations }: Props) {
  const [synthExpanded, setSynthExpanded] = useState(false);

  return (
    <div className="space-y-4">
      {/* AI Synthesis — compact with expand toggle */}
      <div className="rounded-lg border p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
          <p className="text-sm font-semibold">Panel Assessment Summary</p>
          <span className="text-xs text-muted-foreground ml-auto">AI-assisted synthesis</span>
        </div>
        {synthesis ? (
          <div>
            <p
              className={`text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed ${
                synthExpanded ? '' : 'line-clamp-5'
              }`}
            >
              {synthesis}
            </p>
            <button
              type="button"
              onClick={() => setSynthExpanded((v) => !v)}
              className="text-xs text-primary hover:underline mt-1"
            >
              {synthExpanded ? 'Show less' : 'Show more'}
            </button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            Synthesis not available. Panel responses are shown below.
          </p>
        )}
      </div>

      {/* Panel Responses — always expanded, compact inline grid */}
      {deliberations.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Panel Responses ({deliberations.length})
          </p>
          <div className="rounded-lg border divide-y">
            {deliberations.map((d) => {
              const responseMap = new Map(
                (d.responses ?? []).map((r) => [r.questionKey, r.answer])
              );
              return (
                <div key={d.id} className="px-3 py-2.5">
                  {/* Name + submitted/edited time */}
                  <div className="flex items-center gap-3 mb-1.5">
                    <span className="text-sm font-medium">{d.panelistName ?? 'Panel member'}</span>
                    {d.submittedAt && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        {(() => {
                          const submitted = new Date(d.submittedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                          const isEdited = d.updatedAt && d.updatedAt !== d.submittedAt;
                          if (isEdited) {
                            const edited = new Date(d.updatedAt!).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                            return `Submitted ${submitted} · Edited ${edited}`;
                          }
                          return `Submitted ${submitted}`;
                        })()}
                      </span>
                    )}
                  </div>
                  {/* Tick/cross pills — icon + 1-word label per question */}
                  <div className="flex items-center flex-wrap gap-1.5">
                    {QUESTIONS_ORDERED.map((q) => {
                      const answer = responseMap.get(q.key);
                      return (
                        <span
                          key={q.key}
                          title={q.label}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium select-none cursor-default ${
                            answer === true
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                              : answer === false
                              ? 'border-destructive/20 bg-destructive/10 text-destructive'
                              : 'border-border bg-muted text-muted-foreground'
                          }`}
                          aria-label={`${q.label}: ${answer === true ? 'Yes' : answer === false ? 'No' : 'Not answered'}`}
                        >
                          <span aria-hidden="true">{answer === true ? '✓' : answer === false ? '✗' : '–'}</span>
                          {q.short}
                        </span>
                      );
                    })}
                  </div>
                  {/* Overall comment */}
                  {d.overallComment && (
                    <p className="text-xs text-muted-foreground italic mt-1.5 pl-0.5">
                      {d.overallComment}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
